import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "../../../shared/types";
import type { CrawlJob } from "../../../shared/crawl";
import type { PageNode, VoiceDiscoveryDoc, VoiceDiscoveryStatus } from "../../../shared/voice-discovery";
import { SourcesPanel } from "../components/voice/SourcesPanel";
import { ApiError, api } from "../lib/api";

type BrandOutletContext = {
  brand: Brand;
  brandId: string;
};

// Unwrap the { data: T } envelope that voice-discovery endpoints return.
function unwrap<T>(response: { data: T }): T {
  return response.data;
}

export function latestCompletedCrawlJob(jobs: CrawlJob[]): CrawlJob | null {
  return jobs
    .filter((job) => (job.status === "done" || job.status === "partial_success") && job.completed_at)
    .sort((a, b) => new Date(b.completed_at ?? 0).getTime() - new Date(a.completed_at ?? 0).getTime())[0] ?? null;
}

export function crawlBannerStorageKey(brandId: string, latestJobId: string): string {
  return `vd-banner-dismissed-${brandId}-${latestJobId}`;
}

export function shouldShowNewSourcesBanner(args: {
  status: VoiceDiscoveryStatus | "idle";
  docUpdatedAt?: string | null;
  latestCompletedAt?: string | null;
  dismissed: boolean;
}): boolean {
  if (args.status !== "ready") return false;
  if (args.dismissed) return false;
  if (!args.docUpdatedAt || !args.latestCompletedAt) return false;
  return new Date(args.latestCompletedAt).getTime() > new Date(args.docUpdatedAt).getTime();
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="space-y-3">
        <div className="h-3 w-32 rounded bg-slate-200" />
        <div className="h-8 w-3/4 rounded bg-slate-200" />
        <div className="h-4 w-2/3 rounded bg-slate-200" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-[22px] space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-2.5 w-20 rounded bg-slate-200" />
              <div className="h-6 w-12 rounded-full bg-slate-200" />
            </div>
            <div className="h-5 w-40 rounded bg-slate-200" />
            <div className="h-3.5 w-56 rounded bg-slate-200" />
            <div className="rounded-md bg-slate-100 p-3 space-y-1.5">
              <div className="h-3 w-full rounded bg-slate-200" />
              <div className="h-3 w-5/6 rounded bg-slate-200" />
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((j) => (
                <div key={j} className="h-5 w-14 rounded-full bg-slate-200" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── EvidenceGate ──────────────────────────────────────────────────────────────

function EvidenceGate({
  brandId,
  missing,
  onCrawlWebsite,
}: {
  brandId: string;
  missing: string[];
  onCrawlWebsite: () => void;
}) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-5 space-y-3">
      <div className="font-data text-[11px] font-semibold uppercase tracking-wider text-amber-700">
        Evidence needed
      </div>
      <p className="text-[14px] leading-relaxed text-slate-800">
        Voice Discovery needs at least 3 first-party content samples — posts, pages, or product
        copy written by this brand — before it can generate candidates.
      </p>
      {missing.length > 0 && (
        <p className="text-[13px] text-slate-600">
          Tried: {missing.join(", ")}.
        </p>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <Link
          to={`/brand/${brandId}/docs`}
          className="rounded-lg border border-amber-400 bg-amber-100 px-3.5 py-2 text-[13px] font-medium text-amber-900 hover:bg-amber-200"
        >
          Add example posts
        </Link>
        <Link
          to={`/brand/${brandId}/corpus`}
          className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-900 hover:bg-slate-50"
        >
          Upload content
        </Link>
        <button
          type="button"
          onClick={onCrawlWebsite}
          className="rounded-lg border border-brand-300 bg-brand-50 px-3.5 py-2 text-[13px] font-medium text-brand-800 hover:bg-brand-100"
        >
          Crawl your website
        </button>
      </div>
    </div>
  );
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────

function ConfirmDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-xl space-y-4">
        <h3 className="m-0 text-[17px] font-semibold tracking-tight text-slate-900">
          Start over?
        </h3>
        <p className="text-[14px] leading-relaxed text-slate-600">
          This will discard your current candidates and regenerate fresh ones from your evidence.
          Your selected candidate will be lost.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-900 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg border border-red-500 bg-red-500 px-3.5 py-2 text-[13px] font-medium text-white hover:bg-red-600"
          >
            Start over
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Node renderers ────────────────────────────────────────────────────────────

function HeroNode({ data }: { data: Record<string, unknown> }) {
  return (
    <section className="mb-8">
      <div className="font-data mb-3 text-[11px] font-medium uppercase tracking-wider text-brand-600">
        {String(data.eyebrow ?? "")}
      </div>
      <h1 className="m-0 mb-3 text-[clamp(28px,3.4vw,42px)] font-bold leading-[1.08] tracking-tight text-slate-900">
        {String(data.title ?? "")}
      </h1>
      <p className="m-0 max-w-[64ch] text-base leading-relaxed text-slate-500">
        {String(data.subtitle ?? "")}
      </p>
    </section>
  );
}

function CandidateCard({
  data,
  onPick,
}: {
  data: Record<string, unknown>;
  onPick: () => void;
}) {
  const selected = Boolean(data.selected);
  const traits = (data.traits as string[] | undefined) ?? [];
  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border bg-white p-[22px] ${
        selected
          ? "border-brand-500 shadow-[0_0_0_3px_var(--color-brand-100)]"
          : "border-slate-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2.5">
        <span
          className={`font-data text-[10px] uppercase tracking-wider ${
            selected ? "text-brand-700" : "text-slate-500"
          }`}
        >
          {String(data.tag ?? "")}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPick();
          }}
          className={`font-data rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider transition-colors ${
            selected
              ? "bg-brand-600 text-white"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {selected ? "Selected" : "Pick"}
        </button>
      </div>
      <div className="text-lg font-semibold leading-tight tracking-tight">
        {String(data.title ?? "")}
      </div>
      <div className="-mt-2 text-[13px] text-slate-500">{String(data.one ?? "")}</div>
      <div
        className={`rounded-md border-l-2 px-3.5 py-3 text-[14px] leading-relaxed ${
          selected ? "border-brand-500 bg-brand-50" : "border-slate-300 bg-slate-50"
        }`}
      >
        "{String(data.sample ?? "")}"
      </div>
      <div className="flex flex-wrap gap-1.5">
        {traits.map((t) => (
          <span
            key={t}
            className="font-data inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function RationaleNode({ data }: { data: Record<string, unknown> }) {
  const bodies = (data.bodies as string[] | undefined) ?? [];
  function inline(text: string) {
    const parts = text.split(/\[([^\]]+)\]/g);
    return parts.map((p, i) =>
      i % 2 === 1 ? (
        <code
          key={i}
          className="font-data rounded bg-brand-500/15 px-1.5 py-0.5 text-[12px] text-brand-300"
        >
          {p}
        </code>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  }
  return (
    <div className="mb-8 rounded-lg bg-slate-900 px-6 py-[22px] text-slate-200">
      <h4 className="font-data m-0 mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-500">
        {String(data.title ?? "")}
      </h4>
      {bodies.map((b, i) => (
        <p key={i} className="m-0 mb-2 text-[14px] leading-relaxed text-slate-200 last:mb-0">
          {inline(b)}
        </p>
      ))}
      <p className="font-data mt-3 text-[11px] tracking-wider text-slate-400">
        {String(data.pipeline ?? "")}
      </p>
    </div>
  );
}

function PullquoteNode({ data }: { data: Record<string, unknown> }) {
  function inline(text: string) {
    const parts = text.split(/\*\*([^*]+)\*\*/g);
    return parts.map((p, i) =>
      i % 2 === 1 ? (
        <strong key={i} className="font-semibold text-slate-900">
          {p}
        </strong>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  }
  return (
    <div className="mb-8 rounded-r-md border-l-[3px] border-brand-500 bg-brand-50 px-5 py-3.5">
      <div className="font-data mb-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700">
        {String(data.eyebrow ?? "")}
      </div>
      <p className="m-0 text-[15px] leading-relaxed text-slate-800">
        {inline(String(data.body ?? ""))}
      </p>
    </div>
  );
}

// ── Adjustable wrapper + popover ──────────────────────────────────────────────

const SUGGESTIONS: Record<string, string[]> = {
  candidate: ["too sharp", "too generic", "more specific", "drop the corporate edge"],
  hero: ["shorter title", "more concrete", "less academic"],
  rationale: ["explain like I'm a founder", "more technical", "shorter"],
  pullquote: ["more confident", "more humble", "drop the marketing edge"],
};

function Adjustable({
  node,
  regenTarget,
  regenError,
  onAdjust,
  children,
}: {
  node: PageNode;
  regenTarget: string | null;
  regenError: { id: string; message: string } | null;
  onAdjust: (id: string, feedback: string) => Promise<void>;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const isRegen = regenTarget === node.id;
  const error = regenError?.id === node.id ? regenError.message : null;

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (
        wrapRef.current &&
        !wrapRef.current.contains(t) &&
        popRef.current &&
        !popRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!node.adjustable) return <>{children}</>;

  const suggestions = SUGGESTIONS[node.kind] ?? ["shorter", "more specific", "less academic"];

  async function submit() {
    if (!feedback.trim() || busy) return;
    setBusy(true);
    const fb = feedback.trim();
    setFeedback("");
    setOpen(false);
    await onAdjust(node.id, fb);
    setBusy(false);
  }

  return (
    <div
      ref={wrapRef}
      className={`group relative outline outline-1 outline-offset-4 outline-transparent transition-[outline-color] ${
        open ? "outline-2 outline-brand-500" : "hover:outline-brand-300"
      } ${isRegen ? "pointer-events-none" : ""}`}
    >
      <div className={isRegen ? "opacity-50 transition-opacity" : ""}>{children}</div>

      {error && !isRegen && (
        <div className="font-data mt-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-[12px] text-red-700">
          {error}
        </div>
      )}

      {!isRegen && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          title={`Adjust ${node.label ?? "this"}`}
          className={`font-data absolute -right-2.5 -top-2.5 z-10 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white shadow-md transition-transform hover:-translate-y-0.5 hover:bg-brand-600 ${
            open ? "inline-flex" : "hidden group-hover:inline-flex"
          }`}
        >
          <span className="font-semibold text-brand-500 group-hover:text-white">✎</span>
          Adjust {node.label}
        </button>
      )}

      {isRegen && (
        <div className="font-data pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3.5 py-1.5 text-[11px] font-medium text-brand-700 shadow-md">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-600" />
          AI is rewriting…
        </div>
      )}

      {open && (
        <div
          ref={popRef}
          className="absolute right-0 top-[calc(100%+12px)] z-30 grid w-[360px] max-w-[calc(100vw-40px)] gap-3 rounded-lg border border-brand-200 bg-white p-4 shadow-xl"
        >
          <div className="flex items-center justify-between gap-2.5">
            <div className="font-data text-[10px] font-semibold uppercase tracking-wider text-brand-700">
              Refine this node
            </div>
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent px-1 text-lg leading-none text-slate-500 hover:text-slate-900"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 focus-within:border-brand-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-100">
            <textarea
              autoFocus
              placeholder="Tell the AI what's off."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              rows={2}
              className="block w-full resize-none border-none bg-transparent p-0 text-[13px] leading-relaxed text-slate-900 focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFeedback(s)}
                className="font-data cursor-pointer whitespace-nowrap rounded-full border border-dashed border-slate-300 bg-transparent px-2.5 py-1 text-[11px] text-slate-500 transition-colors hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700"
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-900 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!feedback.trim() || busy}
              className="rounded-lg border border-brand-600 bg-brand-600 px-3.5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy ? "Sending…" : "Regenerate"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── JsonRenderer (recursive) ──────────────────────────────────────────────────

function JsonRenderer({
  node,
  regenTarget,
  regenError,
  onAdjust,
  onPick,
}: {
  node: PageNode;
  regenTarget: string | null;
  regenError: { id: string; message: string } | null;
  onAdjust: (id: string, feedback: string) => Promise<void>;
  onPick: (id: string) => void;
}) {
  if (node.kind === "page") {
    return (
      <>
        {(node.children ?? []).map((c) => (
          <JsonRenderer
            key={c.id}
            node={c}
            regenTarget={regenTarget}
            regenError={regenError}
            onAdjust={onAdjust}
            onPick={onPick}
          />
        ))}
      </>
    );
  }

  if (node.kind === "candidate-grid") {
    return (
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {(node.children ?? []).map((c) => (
          <JsonRenderer
            key={c.id}
            node={c}
            regenTarget={regenTarget}
            regenError={regenError}
            onAdjust={onAdjust}
            onPick={onPick}
          />
        ))}
      </div>
    );
  }

  const data = node.data ?? {};
  let content: React.ReactNode = null;
  if (node.kind === "hero") content = <HeroNode data={data} />;
  else if (node.kind === "candidate")
    content = <CandidateCard data={data} onPick={() => onPick(node.id)} />;
  else if (node.kind === "rationale") content = <RationaleNode data={data} />;
  else if (node.kind === "pullquote") content = <PullquoteNode data={data} />;
  if (!content) return null;

  return (
    <Adjustable
      node={node}
      regenTarget={regenTarget}
      regenError={regenError}
      onAdjust={onAdjust}
    >
      {content}
    </Adjustable>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

export function VoiceDiscovery() {
  const ctx = useOutletContext<BrandOutletContext | undefined>();
  const brandId = ctx?.brandId ?? "";
  const brandName = ctx?.brand?.name ?? "this brand";

  const [doc, setDoc] = useState<VoiceDiscoveryDoc | null>(null);
  const [status, setStatus] = useState<VoiceDiscoveryStatus | "idle">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [regenTarget, setRegenTarget] = useState<string | null>(null);
  const [regenError, setRegenError] = useState<{ id: string; message: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [dismissedBannerKey, setDismissedBannerKey] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sourcesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!brandId) return;
    const stored = window.localStorage.getItem(`vd-sources-panel-${brandId}`);
    setSourcesExpanded(stored === "expanded");
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return;
    window.localStorage.setItem(`vd-sources-panel-${brandId}`, sourcesExpanded ? "expanded" : "collapsed");
  }, [brandId, sourcesExpanded]);

  const crawlJobsQuery = useQuery({
    queryKey: ["brands", brandId, "crawl-jobs", "banner"],
    queryFn: () => api.get<{ data: CrawlJob[]; next_cursor?: string }>(`/api/brands/${brandId}/crawl?limit=10`),
    enabled: Boolean(brandId),
    refetchInterval: 5_000,
  });

  const latestCrawlJob = latestCompletedCrawlJob(crawlJobsQuery.data?.data ?? []);
  const bannerKey = latestCrawlJob ? crawlBannerStorageKey(brandId, latestCrawlJob.id) : null;

  useEffect(() => {
    if (!bannerKey) {
      setDismissedBannerKey(null);
      return;
    }
    const dismissed = window.localStorage.getItem(bannerKey) === "dismissed";
    setDismissedBannerKey(dismissed ? bannerKey : null);
  }, [bannerKey]);

  const expandSources = useCallback(() => {
    setSourcesExpanded(true);
    window.setTimeout(() => {
      sourcesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadDoc = useCallback(async () => {
    if (!brandId) return;
    try {
      const data = unwrap(await api.get<{ data: VoiceDiscoveryDoc }>(`/api/brands/${brandId}/voice-discovery`));
      setDoc(data);
      setStatus(data.status);
      setLoadError(null);
      if (data.status !== "generating") stopPoll();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load";
      setLoadError(msg);
      stopPoll();
    }
  }, [brandId, stopPoll]);

  // Initial load + polling while generating
  useEffect(() => {
    if (!brandId) return;
    void loadDoc();
    return stopPoll;
  }, [brandId, loadDoc, stopPoll]);

  useEffect(() => {
    if (status === "generating" && !pollRef.current) {
      pollRef.current = setInterval(() => void loadDoc(), 2000);
    }
    if (status !== "generating") stopPoll();
  }, [status, loadDoc, stopPoll]);

  const handleAdjust = useCallback(
    async (id: string, feedback: string) => {
      if (!brandId || !doc) return;
      setRegenTarget(id);
      setRegenError(null);
      try {
        const updated = unwrap(await api.post<{ data: VoiceDiscoveryDoc }>(
          `/api/brands/${brandId}/voice-discovery/regenerate-node`,
          { nodeId: id, feedback },
        ));
        setDoc(updated);
      } catch (err) {
        let msg = err instanceof Error ? err.message : "Regeneration failed. Try again.";
        if (err instanceof ApiError && err.status === 429) {
          const retryAfter = (err.details as { retry_after_seconds?: unknown } | undefined)?.retry_after_seconds;
          if (typeof retryAfter === "number" && Number.isFinite(retryAfter)) {
            msg = `Rate limited — try again in ${retryAfter}s.`;
          }
        }
        setRegenError({ id, message: msg });
      } finally {
        setRegenTarget(null);
      }
    },
    [brandId, doc],
  );

  const handlePick = useCallback(
    async (id: string) => {
      if (!brandId || !doc) return;
      const prev = doc;
      // Optimistic update
      setDoc((cur) => {
        if (!cur?.page) return cur;
        const updatedPage = applySelectedOptimistic(cur.page, id);
        return { ...cur, page: updatedPage };
      });
      try {
        const updated = unwrap(await api.patch<{ data: VoiceDiscoveryDoc }>(
          `/api/brands/${brandId}/voice-discovery`,
          { selected_candidate_id: id },
        ));
        setDoc(updated);
      } catch {
        setDoc(prev);
      }
    },
    [brandId, doc],
  );

  const handleReset = useCallback(async () => {
    if (!brandId) return;
    setShowConfirm(false);
    setDoc(null);
    setStatus("generating");
    setLoadError(null);
    setRegenError(null);
    try {
      await api.post(`/api/brands/${brandId}/voice-discovery/reset`);
      void loadDoc();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Reset failed";
      setLoadError(msg);
      setStatus("idle");
    }
  }, [brandId, loadDoc]);

  const handleRegenerateForNewSources = useCallback(async () => {
    if (!brandId) return;
    setDoc(null);
    setStatus("generating");
    setLoadError(null);
    setRegenError(null);
    try {
      await api.post(`/api/brands/${brandId}/voice-discovery/reset`);
      void loadDoc();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Reset failed";
      setLoadError(msg);
      setStatus("idle");
    }
  }, [brandId, loadDoc]);

  const showNewSourcesBanner = shouldShowNewSourcesBanner({
    status,
    docUpdatedAt: doc?.updated_at ?? null,
    latestCompletedAt: latestCrawlJob?.completed_at ?? null,
    dismissed: Boolean(bannerKey && dismissedBannerKey === bannerKey),
  });

  // Render states
  if (!brandId) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader brandName={brandName} status={status} onStartOver={undefined} />
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-[14px] text-red-700">
          Brand not found. Navigate to a valid brand to use Voice Discovery.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader brandName={brandName} status={status} onStartOver={() => setShowConfirm(true)} />
        <SourcesPanel
          brandId={brandId}
          expanded={sourcesExpanded}
          onToggle={() => setSourcesExpanded((current) => !current)}
          panelRef={sourcesRef}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-[14px] text-red-700">
          {loadError}
        </div>
      </div>
    );
  }

  if (status === "idle" || (!doc && status !== "generating")) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader brandName={brandName} status={status} onStartOver={() => setShowConfirm(true)} />
        <SourcesPanel
          brandId={brandId}
          expanded={sourcesExpanded}
          onToggle={() => setSourcesExpanded((current) => !current)}
          panelRef={sourcesRef}
        />
        <Skeleton />
      </div>
    );
  }

  if (status === "generating") {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader brandName={brandName} status={status} onStartOver={() => setShowConfirm(true)} />
        <SourcesPanel
          brandId={brandId}
          expanded={sourcesExpanded}
          onToggle={() => setSourcesExpanded((current) => !current)}
          panelRef={sourcesRef}
        />
        <div className="mb-4 flex items-center gap-2.5 rounded-md border border-brand-100 bg-brand-50 px-4 py-3 text-[13px] text-brand-800">
          <span className="h-2 w-2 animate-pulse rounded-full bg-brand-600" />
          Generating your voice candidates — this takes about 15 seconds…
        </div>
        <Skeleton />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader brandName={brandName} status={status} onStartOver={() => setShowConfirm(true)} />
        <SourcesPanel
          brandId={brandId}
          expanded={sourcesExpanded}
          onToggle={() => setSourcesExpanded((current) => !current)}
          panelRef={sourcesRef}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-[14px] text-red-700">
          Generation failed. Check your evidence and try again.
        </div>
      </div>
    );
  }

  if (status === "evidence_required") {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader brandName={brandName} status={status} onStartOver={undefined} />
        <SourcesPanel
          brandId={brandId}
          expanded={sourcesExpanded}
          onToggle={() => setSourcesExpanded((current) => !current)}
          panelRef={sourcesRef}
        />
        <EvidenceGate
          brandId={brandId}
          missing={(doc as VoiceDiscoveryDoc & { missing_first_party_sources?: string[] })
            ?.missing_first_party_sources ?? []}
          onCrawlWebsite={expandSources}
        />
      </div>
    );
  }

  // status === "ready"
  const page = doc?.page;
  if (!page) return null;

  return (
    <>
      {showConfirm && (
        <ConfirmDialog onConfirm={() => void handleReset()} onCancel={() => setShowConfirm(false)} />
      )}
      <div className="flex flex-col gap-4">
        <PageHeader
          brandName={brandName}
          status={status}
          onStartOver={() => setShowConfirm(true)}
        />
        <SourcesPanel
          brandId={brandId}
          expanded={sourcesExpanded}
          onToggle={() => setSourcesExpanded((current) => !current)}
          panelRef={sourcesRef}
        />
        {showNewSourcesBanner && latestCrawlJob && (
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-[13px] text-slate-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <strong className="font-semibold text-slate-900">New sources indexed since these candidates were generated</strong>
                {" "}— regenerate to use them.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleRegenerateForNewSources()}
                  className="rounded-lg border border-brand-600 bg-brand-600 px-3.5 py-2 text-[13px] font-medium text-white hover:bg-brand-700"
                >
                  Regenerate now
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const key = crawlBannerStorageKey(brandId, latestCrawlJob.id);
                    window.localStorage.setItem(key, "dismissed");
                    setDismissedBannerKey(key);
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-900 hover:bg-slate-50"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="min-w-0">
          <div className="mb-6 rounded-md border border-brand-100 bg-brand-50 px-4 py-3 text-[13px] leading-snug text-slate-800">
            <strong className="font-semibold text-slate-900">Hover any block</strong> — title, card,
            rationale, pull-quote — to reveal the{" "}
            <span className="font-data inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] text-white">
              <span className="font-semibold text-brand-500">✎</span>Adjust
            </span>{" "}
            handle. The AI rewrites just that piece.
          </div>
          <JsonRenderer
            node={page}
            regenTarget={regenTarget}
            regenError={regenError}
            onAdjust={handleAdjust}
            onPick={(id) => void handlePick(id)}
          />
        </div>
      </div>
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function PageHeader({
  brandName,
  status,
  onStartOver,
}: {
  brandName: string;
  status: VoiceDiscoveryStatus | "idle";
  onStartOver?: () => void;
}) {
  return (
    <header className="flex items-center justify-between gap-4">
      <div>
        <div className="font-data text-[11px] uppercase tracking-wider text-slate-500">
          {brandName} · Voice Discovery
        </div>
        <h2 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-slate-900">
          Pick a direction
        </h2>
      </div>
      {onStartOver && status === "ready" && (
        <button
          type="button"
          onClick={onStartOver}
          className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-900 hover:bg-slate-50"
        >
          Start over
        </button>
      )}
    </header>
  );
}

function applySelectedOptimistic(node: PageNode, id: string): PageNode {
  if (!node.children) return node;
  return {
    ...node,
    children: node.children.map((c) => {
      if (c.kind === "candidate-grid" && c.children) {
        return {
          ...c,
          children: c.children.map((cc) => ({
            ...cc,
            data: { ...(cc.data ?? {}), selected: cc.id === id },
          })),
        };
      }
      return applySelectedOptimistic(c, id);
    }),
  };
}

export default VoiceDiscovery;
